import React from "react";
import { Col, Row, TabPane } from "reactstrap";
import { CAPACITY_ASSESSMENT_FIELDS, FieldMapping } from "./capacityAssessmentImportFields";

export type CapacityAssessmentsImportWizardTabDataPreviewProps = {
  activeTab: number;
  toggleTab: (tab: number) => void;
  csvHeaders: string[];
  csvRows: Record<string, string>[];
  fieldMappings: FieldMapping;
};

const PREVIEW_LIMIT = 10;

const CapacityAssessmentsImportWizardTabDataPreview: React.FC<
  CapacityAssessmentsImportWizardTabDataPreviewProps
> = ({ activeTab, toggleTab, csvRows, fieldMappings }) => {
  const mappedFields = CAPACITY_ASSESSMENT_FIELDS.filter((f) => !!fieldMappings[f.key]);
  const previewRows = csvRows.slice(0, PREVIEW_LIMIT);

  return (
    <TabPane tabId={3}>
      <div className="d-flex align-items-start gap-3 mb-4 pb-3" style={{ borderBottom: "1px solid #eef0f4" }}>
        <div className="rounded-2 d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: 38, height: 38, background: "#eef2ff" }}>
          <i className="ri-eye-line" style={{ fontSize: 18, color: "#4361ee" }} />
        </div>
        <div className="flex-grow-1">
          <h5 className="mb-0 fw-semibold">Data Preview</h5>
          <p className="text-muted mb-0 mt-1">
            Showing the first {Math.min(PREVIEW_LIMIT, csvRows.length)} of{" "}
            <strong>{csvRows.length.toLocaleString()}</strong> rows based on your field mapping. Review before importing.
          </p>
        </div>
        <span className="badge bg-primary-subtle text-primary rounded-pill px-3 py-2 fs-12 flex-shrink-0 align-self-center">Step 3 of 4</span>
      </div>

      {mappedFields.length === 0 ? (
        <div className="text-center py-5 text-muted">
          <i className="ri-table-line fs-1 mb-2 d-block" />No fields are mapped. Go back and map at least one field.
        </div>
      ) : (
        <Row>
          <Col lg={12}>
            <div className="d-flex align-items-center gap-3 rounded-2 px-3 py-2 mb-3" style={{ background: "#f8f9fc", border: "1px solid #e9ecef" }}>
              <div className="d-flex align-items-center gap-2">
                <i className="ri-table-line text-primary" style={{ fontSize: 15 }} />
                <span><strong>{csvRows.length.toLocaleString()}</strong> total rows</span>
              </div>
              <div className="vr mx-1" style={{ height: 20 }} />
              <div className="d-flex align-items-center gap-2">
                <i className="ri-git-merge-line text-primary" style={{ fontSize: 15 }} />
                <span><strong>{mappedFields.length}</strong> fields mapped</span>
              </div>
              {csvRows.length > PREVIEW_LIMIT && (<><div className="vr mx-1" style={{ height: 20 }} /><span style={{ color: "#64748b" }}>Previewing first {PREVIEW_LIMIT} rows</span></>)}
            </div>

            <div className="rounded-3 border" style={{ maxHeight: 380, overflowY: "auto", overflowX: "auto" }}>
              <table className="table table-sm table-hover align-middle mb-0">
                <thead style={{ background: "#f1f4f9", position: "sticky", top: 0, zIndex: 1 }}>
                  <tr>
                    <th className="py-2 ps-3 text-center" style={{ minWidth: 46, fontWeight: 600, fontSize: 13, color: "#94a3b8", borderBottom: "2px solid #dee2e6" }}>#</th>
                    {mappedFields.map((f) => (
                      <th key={f.key} className="py-2" style={{ whiteSpace: "nowrap", fontWeight: 600, color: "#374151", borderBottom: "2px solid #dee2e6" }}>
                        <div className="d-flex align-items-center gap-1">{f.label}{f.required && <span className="text-danger fw-bold">*</span>}</div>
                        <div className="fw-normal mt-1" style={{ fontSize: 12, color: "#94a3b8" }}>← {fieldMappings[f.key]}</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {previewRows.length === 0
                    ? <tr><td colSpan={mappedFields.length + 1} className="text-center text-muted py-4">No rows to preview.</td></tr>
                    : previewRows.map((row, i) => (
                      <tr key={i} style={{ borderBottom: "1px solid #f1f5f9" }}>
                        <td className="text-center ps-3" style={{ color: "#94a3b8", fontSize: 13 }}>{i + 1}</td>
                        {mappedFields.map((f) => {
                          const col = fieldMappings[f.key];
                          const value = col ? row[col] : undefined;
                          return <td key={f.key} style={{ maxWidth: 220, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: value ? "#1e293b" : "#cbd5e1" }} title={value ?? ""}>{value || "—"}</td>;
                        })}
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </Col>
        </Row>
      )}

      <div className="d-flex align-items-center gap-3 mt-4 pt-3" style={{ borderTop: "1px solid #eef0f4" }}>
        <button type="button" className="btn btn-light btn-label" onClick={() => toggleTab(activeTab - 1)}>
          <i className="ri-arrow-left-line label-icon align-middle fs-16 me-2" />Back
        </button>
        <button type="button" className="btn btn-primary btn-label right ms-auto"
          disabled={mappedFields.length === 0 || previewRows.length === 0} onClick={() => toggleTab(activeTab + 1)}>
          <i className="ri-upload-2-line label-icon align-middle fs-16 ms-2" />Proceed to Import
        </button>
      </div>
    </TabPane>
  );
};

export default CapacityAssessmentsImportWizardTabDataPreview;
